import type {
  AnswerConfidence,
  AnswerEvidence,
  ConversationMessage,
  ConversationMessagePage,
  ConversationParameter,
  DocumentEvidenceRef,
  EvidenceKind,
  ValueSource,
} from "@/api/types";
import type { BadgeTone } from "@/components/StatusBadge";
import { formatQuantity, formatUnit } from "@/lib/format";

/**
 * Comment un contexte de conversation se lit à l'écran (V1.5-01).
 *
 * Séparé du composant pour être testable : ce sont des règles d'affichage, pas
 * du rendu, et elles ont des cas particuliers — une unité de base à traduire,
 * un vocabulaire à mettre en français, des pages qui arrivent à l'envers.
 */

/** Le nom des paramètres tel qu'un humain les lit. */
export const PARAMETER_LABELS: Record<string, string> = {
  material: "Matériau",
  diameter: "Diamètre",
  family: "Famille",
};

export const MATERIAL_LABELS: Record<string, string> = {
  CONCRETE: "béton",
  MASONRY: "maçonnerie",
  STEEL: "acier",
  ALUMINUM: "aluminium",
  WOOD: "bois",
  DRYWALL: "gypse",
  TILE: "céramique",
  PLASTIC: "plastique",
  GLASS: "verre",
};

export const FAMILY_LABELS: Record<string, string> = { DRILL_BIT: "mèche" };

export function parameterLabel(name: string): string {
  return PARAMETER_LABELS[name] ?? name;
}

/**
 * La valeur d'un paramètre, en français et avec son unité.
 *
 * Le backend stocke en unité de base — « 1/2 pouce » y est `12.7 MM`. On
 * affiche ce qu'il a retenu plutôt que ce qui a été tapé : c'est la valeur sur
 * laquelle une recherche portera, et voir « 12,7 mm » après avoir écrit « 1/2
 * pouce » est la seule façon de vérifier qu'elle a été comprise.
 */
export function parameterValue(name: string, value: ConversationParameter): string {
  // `formatQuantity` pour la même raison que partout ailleurs : la base garde
  // « 12.70 », l'écran écrit « 12,7 ». Deux conventions décimales sur le même
  // écran font douter du chiffre.
  if (value.unit) return `${formatQuantity(value.value)} ${formatUnit(value.unit)}`;
  if (name === "material") return MATERIAL_LABELS[value.value] ?? value.value;
  if (name === "family") return FAMILY_LABELS[value.value] ?? value.value;
  return value.value;
}

/**
 * Les messages dans l'ordre de lecture.
 *
 * Les pages arrivent de la plus récente à la plus ancienne — c'est ainsi qu'on
 * remonte un fil — et se lisent dans l'autre sens.
 */
export function messagesFromPages(
  pages: ConversationMessagePage[] | undefined,
): ConversationMessage[] {
  return [...(pages ?? [])].reverse().flatMap((page) => page.items);
}

/** « diameter, material » n'est pas une phrase française. */
export function missingLabels(names: string[]): string {
  return names.map(parameterLabel).join(", ");
}

/**
 * Ce que vaut une réponse, en mots (V1.5-03).
 *
 * Trois classes plutôt qu'un pourcentage : « 92 % » à côté d'une réponse
 * apprend aux gens à ne plus le lire, et un modèle n'a aucun moyen d'en
 * calculer un honnêtement.
 */
export const CONFIDENCE_LABELS: Record<AnswerConfidence, { label: string; tone: BadgeTone }> = {
  CONFIRMED: { label: "Confirmé", tone: "available" },
  LIKELY: { label: "À vérifier", tone: "warning" },
  INSUFFICIENT_EVIDENCE: { label: "Donnée manquante", tone: "unknown" },
};

/** Le rôle d'une preuve, en français. */
export const EVIDENCE_KIND_LABELS: Record<EvidenceKind, string> = {
  FACT: "enregistré",
  SYSTEM_OBSERVED: "lu au ledger",
  OBSERVATION: "vu sur la photo",
  INFERRED: "déduit",
  USER_ASSERTED: "vous l'avez dit",
  DOCUMENT: "lu dans un document",
  MODEL_GENERAL_KNOWLEDGE: "connaissance générale",
};

/** D'où vient la valeur (PM-03), en français. */
export const SOURCE_LABELS: Partial<Record<ValueSource, string>> = {
  HUMAN: "saisi",
  BARCODE: "code-barres",
  OCR: "texte lu",
  LOCAL_VISION: "modèle local",
  CLOUD_VISION: "modèle cloud",
  MANUFACTURER: "fabricant",
  CATALOGUE: "catalogue",
  SUPPLIER: "fournisseur",
  WEB: "web",
  MEASURED: "mesuré",
  IMPORT: "import",
  SYSTEM: "calculé",
};

/**
 * Une preuve, en une ligne lisible.
 *
 * Le nom du champ vient du domaine (`stock.disponible`) ; on garde le dernier
 * segment, qui est celui qui parle.
 */
export function evidenceLine(item: AnswerEvidence): string {
  const segments = item.claim.split(".");
  const claim = (segments[segments.length - 1] ?? item.claim).replace(/_/g, " ");
  const value = item.unit ? `${item.value} ${formatUnit(item.unit)}` : item.value;
  const origin = item.source ? SOURCE_LABELS[item.source] : EVIDENCE_KIND_LABELS[item.kind];
  return origin ? `${claim} : ${value} (${origin})` : `${claim} : ${value}`;
}

/**
 * Une citation documentaire, en une ligne compacte (V1.5-09 §31).
 *
 * « Manuel Milwaukee · p. 14 » — le document et la page, rien de plus. C'est
 * assez pour aller vérifier, et c'est justement ce qu'on demande à une
 * citation. L'extrait complet vit derrière l'ouverture du détail : injecter
 * des pages entières dans le DOM d'un fil de conversation le rendrait
 * illisible sur un téléphone, qui est là où la question se pose.
 */
export function documentCitation(reference: DocumentEvidenceRef): string {
  const parts = [reference.title];
  if (reference.revision) parts.push(`rév. ${reference.revision}`);
  parts.push(`p. ${reference.page}`);
  if (reference.section_title) parts.push(reference.section_title);
  return parts.join(" · ");
}

/**
 * L'avertissement quand l'extrait vient d'une lecture optique.
 *
 * Un scan lu par reconnaissance de caractères se trompe : « 8 » devient « B »,
 * « 0 » devient « O ». Le taire reviendrait à présenter une lecture
 * approximative comme le texte du fabricant.
 */
export function documentReadingNote(reference: DocumentEvidenceRef): string | null {
  return reference.extractor === "tesseract"
    ? "Texte reconnu optiquement sur un document scanné — à relire sur l'original."
    : null;
}

/** L'extrait, coupé pour l'affichage compact et jamais au milieu d'un mot. */
export function shortExcerpt(text: string, limit = 180): string {
  const cleaned = text.replace(/\s+/g, " ").trim();
  if (cleaned.length <= limit) return cleaned;
  const cut = cleaned.slice(0, limit);
  const space = cut.lastIndexOf(" ");
  return `${space > 40 ? cut.slice(0, space) : cut}…`;
}

/** Ce qu'on affiche tant qu'aucun outil n'a encore parlé. */
export const FIRST_STEP = "HD-Vault lit votre question…";

/**
 * Ce qu'on écrit pendant qu'une photo est lue (V1.5-05).
 *
 * Chaque étape a sa phrase parce qu'elles n'ont pas la même durée : le
 * code-barres et l'OCR prennent une seconde, le modèle de vision en prend
 * quinze. Un « analyse en cours… » unique laisserait quelqu'un devant un écran
 * figé sans savoir si l'application travaille ou si elle est morte.
 */
export const VISION_STEPS: Record<string, string> = {
  "attachment.accepted": "Photo reçue…",
  "vision.started": "Je regarde la photo…",
  "barcode.completed": "Codes-barres lus…",
  "ocr.completed": "Texte de l'étiquette lu…",
  "vision.completed": "Analyse de l'image terminée…",
  "matching.started": "Je cherche dans le catalogue…",
  "matching.completed": "Rapprochement terminé…",
};

/** Ce qu'a coûté un tour, quand la vision y a pris sa part. */
export function timingLine(durationMs: number, visionMs: number): string {
  if (visionMs <= 0) return `${(durationMs / 1000).toFixed(1)} s`;
  // La vision est nommée à part : une réponse lente à cause d'un modèle
  // d'image et une réponse lente à cause de l'inventaire ne se règlent pas au
  // même endroit.
  const vision = (visionMs / 1000).toFixed(1);
  return `${(durationMs / 1000).toFixed(1)} s dont ${vision} s de lecture photo`;
}


/**
 * Une recommandation, telle que le backend l'écrit (V1.5-08 §36).
 *
 * Le texte est produit **déterministiquement** par l'orchestrateur, pas par le
 * modèle : sa forme est donc un contrat, et le relire ici est sûr. C'est ce qui
 * permet d'afficher une liste plutôt qu'un paragraphe sans dupliquer la moindre
 * règle de classement côté écran.
 *
 * Un texte qui ne suit pas la forme rend une liste vide, et le message
 * s'affiche tel quel — jamais d'écran cassé parce qu'une phrase a changé.
 */
export interface RecommendedItem {
  rank: number;
  title: string;
  certainty: string;
  reasons: string[];
  warnings: string[];
}

const RANK_LINE = /^(\d+)\.\s+(.+)$/;

export function parseRecommendation(content: string): RecommendedItem[] {
  const items: RecommendedItem[] = [];
  for (const raw of content.split("\n")) {
    const line = raw.trimEnd();
    const rank = RANK_LINE.exec(line.trim());
    if (rank) {
      items.push({
        rank: Number(rank[1]),
        title: rank[2] ?? "",
        certainty: "",
        reasons: [],
        warnings: [],
      });
      continue;
    }
    const current = items[items.length - 1];
    if (!current) continue;
    const body = line.trim();
    if (body.startsWith("· ")) current.reasons.push(body.slice(2));
    else if (body.startsWith("! ")) current.warnings.push(body.slice(2));
    else if (body && !current.certainty) current.certainty = body;
  }
  return items;
}

/** Le ton du badge de certitude d'une recommandation. */
export function recommendationTone(certainty: string): BadgeTone {
  if (certainty.startsWith("Confirmé")) return "available";
  if (certainty.startsWith("Probable")) return "reserved";
  return "warning";
}
